import { toExtra } from '../extra.js';
import { isInAnyGroup } from './group.js';
import { toNumber } from '../utils/utils.js';
import { slotToDateTime, toSlotKeyDecoded } from '../slotKey.js';
const toSiteExtraDef = (data) => {
    if (!Array.isArray(data))
        return [];
    // First convert using toExtra to get the base Extra properties validated
    const baseExtras = toExtra(data);
    // Then add the SiteExtraDef-specific property
    return baseExtras.map((extra, index) => {
        const item = data[index];
        return {
            ...extra,
            maxPrSession: typeof item?.maxPrSession === 'number' && !isNaN(item.maxPrSession)
                ? item.maxPrSession
                : NaN
        };
    });
};
const spaceLinkedLabels = (space) => {
    return space.linked.groupLabel.split(',').map((s) => s.trim());
};
const makeSpace = (overrides = {}) => {
    return {
        id: 'test-id',
        slug: 'test-slug',
        name: 'Test Name',
        display: {
            order: 1,
            groupLabel: ''
        },
        quota: {
            single: 0,
            group: 0,
            groupLabel: ''
        },
        linked: {
            groupLabel: ''
        },
        description: 'Test description',
        active: true,
        extras: [],
        ...overrides
    };
};
/**
 * Get all linked spaces for a given space
 */
const spaceLinked = (space, allSpaces) => {
    if (!space.linked.groupLabel)
        return [];
    const groups = spaceLinkedLabels(space);
    return allSpaces
        .filter((s) => spaceLinkedLabels(s).some((label) => groups.includes(label)))
        .filter((s) => s.id !== space.id);
};
const slotDescription = (slotKeyOrDecoded, siteData) => {
    const slot = typeof slotKeyOrDecoded === 'string'
        ? toSlotKeyDecoded(slotKeyOrDecoded)
        : slotKeyOrDecoded;
    const itemDate = slotToDateTime(slot);
    const siteDescriptions = (siteData?.siteItems || []).filter((sItem) => sItem.id === slot.spaceId);
    const itemName = siteDescriptions.length === 1 ? siteDescriptions[0].name : slot.spaceId;
    const hourDescription = itemDate
        .setLocale(siteData.locale)
        .toFormat('HH:mm MMM dd');
    return `${itemName} ${hourDescription}`;
};
const toSpace = (data) => {
    if (!data.display) {
        data.display = {
            order: toNumber(data.order, 0),
            groupLabel: data.group ?? ''
        };
    }
    if (!data.quota) {
        data.quota = {
            single: toNumber(data.itemQuota, 0),
            group: toNumber(data.groupQuota, 0),
            groupLabel: data.group ?? ''
        };
    }
    if (!data.linked) {
        data.linked = { groupLabel: '' };
    }
    const space = {
        id: data.id ?? '',
        slug: (data.slug ?? data.id ?? '').trim(),
        name: data.name ?? '',
        display: data.display,
        quota: data.quota,
        linked: data.linked,
        description: data.description ?? '',
        active: data.active ?? false,
        // `extras`, not `extra`: the field has always been written as `extras`, so
        // reading the singular silently dropped every unit's equipment on load —
        // and, since the admin page rewrites the whole siteData, erased it on the
        // next save. The singular is still accepted for any document that
        // predates the plural — and because such a document has since been
        // rewritten with an empty `extras`, the singular wins over an empty plural
        // rather than only over a missing one.
        extras: toSiteExtraDef(data.extras?.length ? data.extras : data.extra)
    };
    // Omit the key entirely when unset: an explicit undefined value is
    // rejected by the Firestore client SDK when the whole siteData is saved.
    if (typeof data.accessPointId === 'string' && data.accessPointId !== '') {
        space.accessPointId = data.accessPointId;
    }
    // Same omit-when-unset treatment, and for the same reason the availability
    // record gets it: the whole siteData is rewritten on save, so an empty array
    // on every space is pure weight.
    const restrictedTo = Array.isArray(data.restrictedTo)
        ? data.restrictedTo.filter((id) => typeof id === 'string' && id !== '')
        : [];
    if (restrictedTo.length > 0) {
        space.restrictedTo = restrictedTo;
    }
    return space;
};
/**
 * The spaces this user may see.
 *
 * Deliberately separate from {@link spacesActiveAndSorted}, whose `groupFilter`
 * is a display label grouping *spaces* — overloading it would bury a people
 * group behind an identically named parameter. Hiding a space is presentation
 * only; the booking gate is in the reserve path.
 */
const spacesVisibleToUser = (items, user, now) => {
    return items.filter((space) => isInAnyGroup(user, space.restrictedTo, now));
};
const spaceByIdSlugOrName = (items, key) => {
    // Fist look for slug - then id then name
    // lower case all
    key = key.toLowerCase();
    const bySlug = items.find((item) => item.slug.toLowerCase() === key);
    if (bySlug) {
        return bySlug;
    }
    const byId = items.find((item) => item.id.toLowerCase() === key);
    if (byId) {
        return byId;
    }
    return items.find((item) => item.name.toLowerCase() === key);
};
const spacesActiveAndSorted = (items, groupFilter) => {
    items.sort((a, b) => {
        if (a.display.order === b.display.order) {
            return (a.name || '').localeCompare(b.name || '');
        }
        else {
            return a.display.order - b.display.order;
        }
    });
    if (groupFilter) {
        return items
            .filter((item) => item.display.groupLabel === groupFilter)
            .filter((item) => item.active);
    }
    return items.filter((item) => item.active);
};
const appliesToSpace = (spaceFilter, spaceId) => {
    if (!spaceFilter || spaceFilter === '')
        return true;
    return spaceFilter.split(',').includes(spaceId);
};
export { slotDescription, spaceByIdSlugOrName, spaceLinked, spaceLinkedLabels, spacesActiveAndSorted, spacesVisibleToUser, toSpace, appliesToSpace, makeSpace };
//# sourceMappingURL=space.js.map